// ============================================
// AvatarInitials Component
// Circle with tinted background + 2-letter initials
// ============================================

import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

interface AvatarInitialsProps {
  /** Full name to extract initials from */
  name: string;
  /** Seed for deterministic color (e.g., customer ID) */
  colorSeed?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  className?: string;
}

// ============================================
// Color Palette
// ============================================

const avatarColors = [
  'bg-blue-500/10 text-blue-700',
  'bg-orange-500/10 text-orange-700',
  'bg-indigo-500/10 text-indigo-700',
  'bg-emerald-500/10 text-emerald-700',
  'bg-purple-500/10 text-purple-700',
  'bg-pink-500/10 text-pink-700',
  'bg-cyan-500/10 text-cyan-700',
  'bg-amber-500/10 text-amber-700',
];

// Special colors for specific states
const pendingColor = 'bg-[#FFDAD6] text-red-700';

// ============================================
// Helpers
// ============================================

/** Extract initials from full name */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/** Get deterministic color based on seed */
function getColorFromSeed(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

// ============================================
// Component
// ============================================

export function AvatarInitials({
  name,
  colorSeed,
  size = 'md',
  className,
}: AvatarInitialsProps) {
  const initials = getInitials(name);
  const colorClass = colorSeed
    ? colorSeed.toLowerCase().includes('pending')
      ? pendingColor
      : getColorFromSeed(colorSeed)
    : getColorFromSeed(name);

  const sizeClass = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-9 w-9 text-sm';

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full font-bold',
        sizeClass,
        colorClass,
        className
      )}
    >
      {initials}
    </div>
  );
}
